import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { green, red } from "@mui/material/colors";
import { Helmet } from "react-helmet-async";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
} from "../../../Styles/Styles";
import IDRejectReasonModal from "./IDRejectReasonModal";

export default function IDsManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const fetchUsersWithID = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/with-government-id`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateIDStatus = async (userId, status, reason = "") => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/${status}-id/${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ rejectReason: reason }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${status} ID.`);
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, isVerifiedbyID: status } : user
        )
      );
    } catch (err) {
      console.error(`Error updating ID status to ${status}:`, err);
    }
  };

  const handleRejectClick = (userId) => {
    setSelectedUserId(userId);
    setModalOpen(true);
  };

  const handleConfirmReject = () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    updateIDStatus(selectedUserId, "rejected", rejectReason);
    setModalOpen(false); // Close the modal after confirming rejection
    setRejectReason(""); // Clear the reject reason after submission
  };

  const handleDeleteID = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this ID?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/delete-id/${userId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to delete ID");

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId
            ? { ...user, governmentId: null, isVerifiedbyID: "pending" }
            : user
        )
      );
      alert("Government ID deleted successfully.");
    } catch (error) {
      console.error("Error deleting ID:", error);
      alert("Error deleting ID. Please try again.");
    }
  };

  useEffect(() => {
    fetchUsersWithID();
  }, []);

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>ID Verification - Listed.ky</title>
        <meta
          name="description"
          content="Manage user ID verification on Listed.ky"
        />
      </Helmet>

      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        ID Verification Management
      </Typography>

      {error ? (
        <Typography
          variant="h6"
          color="error"
          sx={{ textAlign: "center", mt: 4 }}
        >
          {error}
        </Typography>
      ) : (
        <TableContainer sx={{ ...cardOutline }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headingFonts }}>Name</TableCell>
                <TableCell sx={{ ...headingFonts }}>Email</TableCell>
                <TableCell sx={{ ...headingFonts }}>Phone</TableCell>
                <TableCell sx={{ ...headingFonts }}>Government ID</TableCell>
                <TableCell sx={{ ...headingFonts }}>Status</TableCell>
                <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell sx={{ ...headingFonts }}>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell sx={{ ...fonts }}>{user.email}</TableCell>
                    <TableCell sx={{ ...fonts }}>
                      {user.countryCode} {user.phoneNumber}
                    </TableCell>
                    <TableCell sx={{ ...fonts }}>
                      {user.governmentId?.frontImage && (
                        <a
                          href={user.governmentId.frontImage}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginRight: "10px" }}
                        >
                          Front ID
                        </a>
                      )}
                      {user.governmentId?.backImage && (
                        <a
                          href={user.governmentId.backImage}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Back ID
                        </a>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...fonts }}>
                      {user.isVerifiedbyID}
                      <br />
                      {user.isVerifiedbyID === "rejected" &&
                        user.IDRejectionReason && (
                          <Typography variant="body2" sx={{ color: "red" }}>
                            ({user.IDRejectionReason})
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => updateIDStatus(user._id, "approved")}
                        sx={{ color: green[500], ...noOutline }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRejectClick(user._id)}
                        sx={{ color: red[500], ...noOutline }}
                      >
                        <CancelIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteID(user._id)}
                        sx={{ color: "grey", ...noOutline }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ ...fonts, textAlign: "center" }}>
                    No users have submitted a government ID.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <IDRejectReasonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        reason={rejectReason}
        setReason={setRejectReason}
        onConfirm={handleConfirmReject}
      />
    </Box>
  );
}
